import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetPaymentTermDocument,
  UpdatePaymentTermDocument,
  DeletePaymentTermDocument,
  type GetPaymentTermQuery,
  type UpdatePaymentTermMutation,
  type UpdatePaymentTermMutationVariables,
  type DeletePaymentTermMutation,
  type DeletePaymentTermMutationVariables,
} from '@/generated/root-types';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { useNotification } from '@/app/contexts/NotificationContext';
import { errorsFromPayload } from '@/utils/mutationErrors';
import {
  detailBackButtonSx,
  detailFieldGridSx,
  detailFieldSx,
  detailFullRowSx,
  detailHeaderRowSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

export default function PaymentTermDetail() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const superAdmin = isSuperAdmin(user);

  const [name, setName] = useState('');
  const [days, setDays] = useState('0');
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetPaymentTermQuery>(GetPaymentTermDocument, {
    variables: { id: id! },
    skip: !id,
  });

  const pt = data?.paymentTerm;

  useEffect(() => {
    if (!pt) return;
    setName(pt.name);
    setDays(String(pt.days));
    setIsActive(pt.isActive);
    setIsDefault(pt.isDefault);
  }, [pt]);

  const [updatePt, { loading: saving }] = useMutation<
    UpdatePaymentTermMutation,
    UpdatePaymentTermMutationVariables
  >(UpdatePaymentTermDocument);

  const [deletePt, { loading: deleting }] = useMutation<
    DeletePaymentTermMutation,
    DeletePaymentTermMutationVariables
  >(DeletePaymentTermDocument);

  const handleSave = async () => {
    if (!id) return;
    const d = parseInt(days, 10);
    if (Number.isNaN(d)) {
      showError(t('errors.generic'));
      return;
    }
    try {
      const res = await updatePt({
        variables: { input: { id, name, days: d, isActive, isDefault } },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.updatePaymentTerm);
      if (pe) {
        showError(pe);
        return;
      }
      showSuccess(t('common.saved'));
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      const res = await deletePt({ variables: { input: { id } } });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.deletePaymentTerm);
      if (pe || !res.data?.deletePaymentTerm?.boolean) {
        showError(pe || t('errors.generic'));
        return;
      }
      showSuccess(t('common.deleted'));
      navigate('/payment-terms');
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ ...detailPageContainerSx, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !pt) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/payment-terms')}>
          {t('common.back')}
        </Button>
        <Typography color="error" sx={{ mt: 2 }}>
          {error?.message ?? t('errors.generic')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={detailHeaderRowSx}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/payment-terms')}
          sx={detailBackButtonSx}
        >
          {t('common.back')}
        </Button>
        <Typography variant="h4" component="h1" sx={detailTitleSx}>
          {pt.name}
        </Typography>
      </Box>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, width: '100%', boxSizing: 'border-box' }}
      >
        <Box sx={detailFieldGridSx}>
          <TextField
            sx={detailFieldSx}
            label={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('masterData.days')}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            type="number"
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.created')}
            value={formatDateTime(pt.createdAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.updated')}
            value={formatDateTime(pt.updatedAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />

          <FormControlLabel
            sx={{ ...detailFullRowSx, ml: 0 }}
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={!superAdmin}
              />
            }
            label={t('masterData.active')}
          />
          <FormControlLabel
            sx={{ ...detailFullRowSx, ml: 0 }}
            control={
              <Switch
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                disabled={!superAdmin}
              />
            }
            label={t('common.default')}
          />
          {superAdmin && (
            <Box sx={{ ...detailFullRowSx, display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {t('common.save')}
              </Button>
              <Button color="error" onClick={() => setDeleteOpen(true)}>
                {t('common.delete')}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t('masterData.confirmDeleteTerm')}</DialogTitle>
        <DialogContent>
          <Typography>{t('masterData.confirmDeleteBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
